import type { SoundName, SoundSpec } from './sounds';
import { SOUND_LIBRARY } from './sounds';

/**
 * Thin Web Audio wrapper that schedules `SoundSpec` cues onto a single
 * `AudioContext` with a master gain stage.
 *
 * Why class + lazy context:
 *
 *  - Browsers refuse to start an `AudioContext` until they observe a user
 *    gesture, so we *cannot* construct one at app boot. We construct on the
 *    first `play()` call instead, then `resume()` on every subsequent call
 *    in case the tab was backgrounded (mobile Safari pauses the context).
 *  - The class form lets `setMuted()` and `play()` share the same context +
 *    master gain without leaning on module-level mutable state.
 *
 * `setMuted(true)` short-circuits inside `play()` *before* doing any context
 * work — a muted toggle should not even cause an `AudioContext` to spin up.
 *
 * Note: AudioContext.resume() can fail silently if the iframe never received
 * a user gesture. That's the platform contract, not our bug — the next click
 * anywhere in the iframe will unblock it.
 */
export class ActivityAudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private muted: boolean;
  private readonly masterVolume: number;

  constructor({ initiallyMuted, masterVolume = 0.3 }: { initiallyMuted: boolean; masterVolume?: number }) {
    this.muted = initiallyMuted;
    this.masterVolume = masterVolume;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setTargetAtTime(
        muted ? 0 : this.masterVolume,
        this.ctx.currentTime,
        0.01,
      );
    }
  }

  play(name: SoundName): void {
    if (this.muted) return;
    const spec = SOUND_LIBRARY[name];
    const ready = this.ensureRunning();
    if (!ready) return;
    this.schedule(spec);
  }

  /**
   * Tear down the AudioContext when the provider unmounts. Safari leaks
   * suspended contexts otherwise — closing them is the documented contract.
   */
  async close(): Promise<void> {
    if (this.ctx) {
      const ctx = this.ctx;
      this.ctx = null;
      this.masterGain = null;
      await ctx.close();
    }
  }

  private ensureRunning(): boolean {
    if (typeof window === 'undefined' || typeof window.AudioContext === 'undefined') {
      return false;
    }
    if (!this.ctx) {
      this.ctx = new window.AudioContext();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.muted ? 0 : this.masterVolume;
      this.masterGain.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') {
      void this.ctx.resume();
    }
    return this.ctx.state !== 'closed';
  }

  private schedule(spec: SoundSpec): void {
    const ctx = this.ctx!;
    const master = this.masterGain!;
    const startAt = ctx.currentTime;

    for (const note of spec.notes) {
      const osc = ctx.createOscillator();
      osc.type = note.type;
      osc.frequency.value = note.freq;

      const noteGain = ctx.createGain();
      const t0 = startAt + note.delay;
      const tAttack = t0 + note.attack;
      const tSustain = tAttack + note.sustain;
      const tRelease = tSustain + note.release;

      noteGain.gain.setValueAtTime(0.0001, t0);
      noteGain.gain.linearRampToValueAtTime(note.peak, tAttack);
      noteGain.gain.setValueAtTime(note.peak, tSustain);
      noteGain.gain.exponentialRampToValueAtTime(0.0001, tRelease);

      osc.connect(noteGain).connect(master);
      osc.start(t0);
      osc.stop(tRelease + 0.05);
    }
  }
}
